'use client';
import { useState, FormEvent } from 'react';
import { Box, Link } from '@mui/material';
import axios from 'axios';
import { useGoogleLogin as useGoogleOAuth } from '@react-oauth/google';
import { AuthButton } from '../AuthButton/AuthButton';
import { AuthFormWrapper } from '../AuthFormWrapper/AuthFormWrapper';
import { AuthInput } from '../AuthInput/AuthInput';
import { Checkbox, useToast, GoogleIcon } from '@practium/ui';
import styles from './LoginForm.module.css';
import type { LoginCredentials } from '../../types/auth.types';
import { useLogin, useGoogleLogin, isConsentRequired, isAnonymizationPending } from '@practium/features/api';
import type { AnonymizationPendingError } from '@practium/features/api';
import { LoginConsentGate } from '../ConsentGate/LoginConsentGate';
import { AnonymizationGate } from '../AnonymizationGate/AnonymizationGate';
import type { AnonymizationGateCredentials } from '../AnonymizationGate/AnonymizationGate';

function extractApiErrorMessage(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error) && error.response?.data?.message) {
		return error.response.data.message;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
}

interface PendingConsent {
	token: string;
	rememberMe: boolean;
}

interface PendingAnonymization {
	error: AnonymizationPendingError;
	credentials: AnonymizationGateCredentials | null;
}

interface GoogleLoginButtonProps {
	onConsentRequired: (pending: PendingConsent) => void;
	onAnonymizationPending: (error: AnonymizationPendingError) => void;
	onSuccess: () => void;
	disabled: boolean;
}

const GoogleLoginButton = ({ onConsentRequired, onAnonymizationPending, onSuccess, disabled }: GoogleLoginButtonProps) => {
	const toast = useToast();
	const { mutate: googleLogin, isPending } = useGoogleLogin();

	const triggerGooglePopup = useGoogleOAuth({
		scope: 'openid email profile',
		onSuccess: (codeResponse) => {
			googleLogin(codeResponse.code, {
				onSuccess,
				onError: (err) => {
					if (isConsentRequired(err)) {
						onConsentRequired({ token: (err as any).consentToken, rememberMe: true });
						return;
					}
					if (isAnonymizationPending(err)) {
						onAnonymizationPending(err);
						return;
					}
					toast.error(extractApiErrorMessage(err, 'Erro ao autenticar com o Google'));
				},
			});
		},
		onError: () => {
			toast.error('Não foi possível abrir a janela de login do Google.');
		},
		flow: 'auth-code',
	});

	return (
		<AuthButton
			variant="social"
			fullWidth
			icon={<GoogleIcon />}
			onClick={() => triggerGooglePopup()}
			disabled={disabled || isPending}
			isLoading={isPending}
		>
			Continuar com Google
		</AuthButton>
	);
};

interface LoginFormProps {
	onSuccess?: () => void;
}

export const LoginForm = ({ onSuccess }: LoginFormProps = {}) => {
	const toast = useToast();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(null);
	const [pendingAnonymization, setPendingAnonymization] = useState<PendingAnonymization | null>(null);

	const { mutate: login, isPending } = useLogin();

	const hasGoogleClient = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

	const handleSuccess = () => {
		toast.success('Login realizado com sucesso!');
		if (onSuccess) {
			onSuccess();
		} else {
			globalThis.location.href = '/dashboard';
		}
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();

		const credentials: LoginCredentials = { email, password, rememberMe };

		login(credentials, {
			onSuccess: handleSuccess,
			onError: (err) => {
				if (isConsentRequired(err)) {
					setPendingConsent({ token: (err as any).consentToken, rememberMe });
					return;
				}
				if (isAnonymizationPending(err)) {
					setPendingAnonymization({ error: err, credentials: { email, password } });
					return;
				}
				toast.error(extractApiErrorMessage(err, 'Erro ao fazer login'));
			},
		});
	};

	const handleReactivated = (credentials: AnonymizationGateCredentials) => {
		setPendingAnonymization(null);
		login(
			{ email: credentials.email, password: credentials.password, rememberMe },
			{
				onSuccess: handleSuccess,
				onError: (err) => {
					if (isConsentRequired(err)) {
						setPendingConsent({ token: (err as any).consentToken, rememberMe });
						return;
					}
					toast.error(extractApiErrorMessage(err, 'Erro ao fazer login'));
				},
			}
		);
	};

	return (
		<>
			<AuthFormWrapper
				onSubmit={handleSubmit}
				socialButtons={
					<>
						{hasGoogleClient ? (
							<GoogleLoginButton
								onSuccess={handleSuccess}
								onConsentRequired={setPendingConsent}
								onAnonymizationPending={(error) =>
									setPendingAnonymization({ error, credentials: null })
								}
								disabled={isPending}
							/>
						) : (
							<AuthButton variant="social" fullWidth icon={<GoogleIcon />} disabled>
								Continuar com Google
							</AuthButton>
						)}
					</>
				}
			>
				<div className={styles.inputWrapper}>
					<AuthInput
						label="E-mail"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="exemplo@gmail.com"
						required
					/>
					<AuthInput
						label="Senha"
						isPassword
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						placeholder="abc123"
						required
					/>
				</div>

				<Box className={styles.optionsRow}>
					<Checkbox
						label="Lembrar-me"
						checked={rememberMe}
						onChange={(e) => setRememberMe(e.target.checked)}
					/>
					<Link href="/forgot-password" className={styles.forgotLink}>
						Esqueceu a senha?
					</Link>
				</Box>

				<AuthButton type="submit" variant="primary" fullWidth isLoading={isPending}>
					Fazer Login
				</AuthButton>
			</AuthFormWrapper>

			{pendingConsent && (
				<LoginConsentGate
					token={pendingConsent.token}
					rememberMe={pendingConsent.rememberMe}
					onComplete={handleSuccess}
				/>
			)}

			{pendingAnonymization && (
				<AnonymizationGate
					info={pendingAnonymization.error}
					credentials={pendingAnonymization.credentials}
					onReactivated={handleReactivated}
					onClose={() => setPendingAnonymization(null)}
				/>
			)}
		</>
	);
};
